import React from "react";

import * as Comp from "../../../../components";
import { NavigationTypes } from "../../../../domain";
import * as Forms from "../../../../forms";
import * as M from "../../../../models";
import { Api } from "../../../../services/Api";
import Settings from "../../../../settings";
import { Utility } from "../../../../utils";

interface OwnProps {
    api: Api;
    onCancel?: () => void;
    onError?: (error: string) => void;
    onProcessing?: () => void;
    onUpdated?: (navItem: M.AdminNav.NavListItem) => void;
    navItemsGetter: () => M.AdminNav.NavListItem[];
}

interface OwnState {
    formModel: Forms.AdminNav.NavItemEdit;
    navItem?: M.AdminNav.NavListItem;
    formValuesSet: boolean;
    show: boolean;
}

export class EditNavItemModal extends Comp.Validation.ComponentWithValidatedForm<OwnProps, OwnState> {

    private navTypeMap = Utility.createNumberIndexMap(NavigationTypes.Values, (t) => t.id);

    public constructor(props: any) {
        super(props);

        const formModel = new Forms.AdminNav.NavItemEdit();
        this.state = {
            ...this.getInitialState(formModel),
            formValuesSet: false,
            show: false,
        };

        this.onSubmit = this.onSubmit.bind(this);
    }

    public shouldComponentUpdate(nextProps: Readonly<OwnProps>, nextState: Readonly<OwnState>, nextContext: any): boolean {
        // const currItems = this.props.navItemsGetter();
        const nextItems = nextProps.navItemsGetter();

        if (this.state.navItem && !this.state.formValuesSet && nextItems.length) {
            // nav items received
            // we need to set the parent select field value
            const formModel = { ...this.state.formModel };
            const parentIds = this.state.navItem.parentIds;
            const parents = nextItems.filter((n) => this.state.navItem
                && parentIds.length
                && parentIds[parentIds.length - 1] === n.id);

            if (parents.length) {
                const parent = parents[0];
                formModel.data.parentId = Utility.toReactSelectOption(parent, parent.title, parent.id);
            }

            this.setState({ ...this.state, formModel, formValuesSet: true });
            return false;
        }

        return true;
    }

    public close() {
        this.setState({
            ...this.state,
            ...this.getInitialState(this.navItemToFormModel()),
            formValuesSet: false,
            navItem: undefined,
            show: false });
    }

    public show(navItem?: M.AdminNav.NavListItem) {
        const formModel = this.navItemToFormModel(navItem);

        this.setState({
            ...this.state,
            ...this.getInitialState(formModel),
            navItem,
            show: true,
        });

        if (navItem)
            this.loadRelatedRecord(navItem.typeId, navItem.recordId);
    }

    public render() {
        const isNew = this.state.formModel.data.id === 0;
        const isLocked = this.state.formModel.data.locked;

        // setup url editor.
        // default if new item
        let urlEditor = null;

        if (isNew) {
            const newUrl = this.state.formModel.data.url.changeUrl
                ? <Comp.Editors.TextField formComponent={this} name="url.newUrl" hint="This will override the suggested URL and is not a redirect" css="txt" placeholder="/..." />
                : null;

            urlEditor = <div>
                <Comp.Editors.FormField name="url" label="URL">
                    {this.state.formModel.data.url.currentUrl}
                    <Comp.Editors.CheckboxField formComponent={this} label="Override URL" name="url.changeUrl" noFieldWrap />
                </Comp.Editors.FormField>
                {newUrl}
            </div>;
        }
        else if (isLocked) {
            // if an existing item is locked
            urlEditor = <Comp.Editors.FormField name="url" label="URL">
                {this.state.formModel.data.url.currentUrl}
            </Comp.Editors.FormField>;
        }
        else {
            // otherwise editing an existing record
            const updateChildren = this.state.formModel.data.url.changeUrl
                ? <Comp.Editors.CheckboxField
                    formComponent={this}
                    name="url.updateChildren"
                    noFieldWrap
                    hint="If checked, will attempt to update the URLs of all of this item's children. Note: this update may take a while."
                />
                : null;
            const newUrl = this.state.formModel.data.url.changeUrl
                ? <Comp.Editors.TextField formComponent={this} name="url.newUrl" hint="This will override the suggested URL and is not a redirect" css="txt" />
                : null;

            urlEditor = <div>
                <Comp.Editors.FormField name="" label="URL">
                    {this.state.formModel.data.url.currentUrl}
                    <Comp.Editors.CheckboxField formComponent={this} name="url.changeUrl" noFieldWrap />
                    {updateChildren}
                </Comp.Editors.FormField>

                {newUrl}
            </div>;
        }

        return <Comp.Modal.ModalForm
            bsSize="lg"
            formComponent={this}
            onCancel={() => this.handleCancel()}
            processing={this.state.formProcessing}
            show={this.state.show}
            title={(isNew ? "Add new" : "Edit") + " navigation item"}
        >
            <Comp.Validation.ValidatedForm
                formComponent={this}
                model={this.state.formModel.data}
                onValidSubmit={this.onSubmit}
                schema={this.state.formModel.rules}
                ref={(ref: any) => this.form = ref}
            >
                <Comp.Editors.Hidden formComponent={this} name="typeId" />
                <Comp.Editors.CheckboxField formComponent={this} name="active" />
                <Comp.Editors.CheckboxField formComponent={this} name="onMenu" />
                <Comp.Editors.TextField formComponent={this} name="title" css="txt" onInputChange={(inputName: string, value: string) => this.onTitleChange(value)} />

                <Comp.Editors.SelectField
                    formComponent={this}
                    isAsync
                    name="parentId"
                    onInputChange={(inputName, value) => this.onParentChange(value)}
                    select={{
                        cache: false,
                        loadOptions: (search: string) => this.searchNavItems(search),
                        optionRenderer: (n: M.AdminNav.NavListItem) => <span>{n.title}<br /><span className="mute">{n.fullTitle}</span></span>,
                    }}
                />

                <Comp.Editors.SelectField
                    formComponent={this}
                    isAsync
                    name="relatedRecordId"
                    onInputChange={(inputName, value) => this.onRelatedRecordChange(value)}
                    select={{
                        // cache: false,
                        loadOptions: (search: string) => this.searchRelatedRecords(search),
                        optionRenderer: (r: M.AdminNav.NavigatableRecordItem) => <span>{r.title}</span>,
                        valueRenderer: (r: M.AdminNav.NavigatableRecordItem) => <span>{r.title}</span>,
                    }}
                />

                {urlEditor}

            </Comp.Validation.ValidatedForm>
        </Comp.Modal.ModalForm>;
    }

    /**
     * If provided, converts the nav item into a form model. Otherwise returns a new form model.
     * @param [navItem] The nav item
     * @returns M.AdminNav.NavListItem
     */
    private navItemToFormModel(navItem?: M.AdminNav.NavListItem) {
        const formModel = new Forms.AdminNav.NavItemEdit();
        if (!navItem)
            return formModel;
        else {
            formModel.data.active = navItem.statusId === 1;
            formModel.data.id = navItem.id;
            formModel.data.locked = navItem.locked;
            formModel.data.onMenu = navItem.onNav;
            formModel.data.title = navItem.title;
            formModel.data.url.currentUrl = navItem.url;
            formModel.data.url.newUrl = navItem.url;
            return formModel;
        }
    }

    private handleCancel() {
        if (this.props.onCancel)
            this.props.onCancel();

        this.close();
    }

    private async loadRelatedRecord(typeId: number, recordId: number) {
        if (recordId > 0 && NavigationTypes.isContentType(typeId)) {
            const qp = { queryParams: { typeId, recordId } };
            const result = await this.props.api.callApi<M.AdminNav.NavigatableRecordItem>(Api.endpoints.admin.nav.navigatableRecord, qp);

            if (result.isSuccess && result.data) {
                const d = result.data;
                const type = this.navTypeMap[typeId];
                const title = d.title + (!type || type.name === "Page" ? "" : (" (" + type.name + ")"));
                const formModel = Object.assign({}, this.state.formModel);
                formModel.data.relatedRecordId = Utility.toReactSelectOption(d, title, d.id);
                this.setState({ ...this.state, formModel });
            }
        }
    }

    private async onSubmit(model: Forms.AdminNav.NavItemEditData) {
        this.toggleProcessing(true);

        const updateResult = await this.props.api.put<M.AdminNav.NavListItem>(Api.endpoints.admin.nav.item, {
            body: JSON.stringify({
                ...model,
                appId: Settings.AppID,
                parentId: model.parentId === null ? null : (model.parentId as M.AdminNav.NavListItem).id,
                relatedRecordId: model.relatedRecordId === null ? null : (model.relatedRecordId as M.AdminNav.NavigatableRecordItem).id,
            }),
        });

        if (updateResult.isSuccess && updateResult.data) {
            if (this.props.onUpdated)
                this.props.onUpdated(updateResult.data);
            this.close();
        }
        else {
            if (this.props.onError)
                this.props.onError(updateResult.errors.join("; "));

            this.setFormErrors(updateResult.errors.length ? updateResult.errors : [updateResult.message || "Server error"]);
        }

        this.toggleProcessing(false);
    }

    private onParentChange(parent: M.AdminNav.NavListItem) {
        const formModel = Object.assign({}, this.state.formModel);
        this.updateNewUrl(parent === null ? "" : parent.url, formModel.data.title);
    }

    private onRelatedRecordChange(record: M.AdminNav.NavigatableRecordItem) {
        const formModel = Object.assign({}, this.state.formModel);
        formModel.data.typeId = record === null ? 0 : record.typeId;
        this.setState({ ...this.state, formModel });
    }

    private onTitleChange(title: string) {
        const formModel = Object.assign({}, this.state.formModel) as Forms.AdminNav.NavItemEdit;
        if (!formModel.data.locked) {
            const parentUrl = formModel.data.parentId && (formModel.data.parentId as M.AdminNav.NavListItem).url
                ? (formModel.data.parentId as M.AdminNav.NavListItem).url
                : "";
            this.updateNewUrl(parentUrl, title);
        }
    }

    private searchNavItems(search: string) {
        if (!search || search.length < 2)
            return Promise.resolve({ options: [] });

        const navItems = this.props.navItemsGetter()
            .filter((n) => {
                return n.title.toLowerCase().indexOf(search.toLowerCase()) > -1
                    && NavigationTypes.isContentType(n.typeId) // must include only nav items that can have children
                    && [n.id].concat(n.parentIds).indexOf(this.state.formModel.data.id) === -1; // must exclude nav items who are children of the item being edited.
            })
            .map((n) => (Utility.toReactSelectOption(n, n.title, n.id)));

        const scoredItems = Utility.sortFilteredArrayByRelevance(navItems, (n) => n.title, search, ["title"]);

        return new Promise((resolve, reject) => {
            resolve({
                options: scoredItems,
            });
        });
    }

    private searchRelatedRecords(search: string) {
        const placeholder = Utility.toReactSelectOption({ id: 0, typeId: 0, title: "(Placeholder)" }, search, 0);

        if (!search || search.length < 2)
            return Promise.resolve({ options: [placeholder] });

        const queryParams = { queryParams: { q: search } };
        return this.props.api.callApi<M.AdminNav.NavigatableRecordItem[]>(Api.endpoints.admin.nav.searchNavigatableRecords, queryParams)
            .then((result) => {
                if (result.isSuccess && result.data) {
                    let options = result.data.map((d) => {
                        const type = this.navTypeMap[d.typeId];
                        const title = d.title + (!type || type.name === "Page" ? "" : (" (" + type.name + ")"));
                        return Utility.toReactSelectOption(d, title, d.id);
                    });
                    options = [placeholder, ...options];
                    return { options };
                }
                else
                    throw new Error(result.errors.join("; "));
            });
    }

    private updateNewUrl(parentUrl: string, title: string) {
        const formModel = Object.assign({}, this.state.formModel);
        formModel.data.url.currentUrl = parentUrl + (parentUrl === "/" ? "" : "/") + Utility.toSlug(title);
        if (!formModel.data.url.changeUrl)
            formModel.data.url.newUrl = formModel.data.url.currentUrl;
        this.setState({ ...this.state, formModel });
    }
}



// WEBPACK FOOTER //
// ./src/containers/adminNav/list/components/EditNavItemModal.tsx